import { useMemo, useState } from 'react';
import {
    Package,
    TrendingUp,
    TrendingDown,
    BarChart3,
    Trophy,
    AlertTriangle,
    ShoppingBag,
    Layers,
    Filter,
    MapPin,
    Store,
    Hash,
    ToggleLeft,
    ToggleRight,
} from 'lucide-react';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';


import KPICard from '../components/common/KPICard';
import ChartCard from '../components/common/ChartCard';
import DataTable from '../components/common/DataTable';
import InsightCard from '../components/common/InsightCard';
import ProductBreakdownDrawer from '../components/common/ProductBreakdownDrawer';
import { useFilters } from '../context/FilterContext';
import { applyMultiLevelFilters } from '../utils/filters';
import { formatCurrency, formatNumber, formatPercentage } from '../utils/formatters';
import {
    getTopCityStorePincodeByMetric,
    getProductDistributionByCity,
} from '../utils/salesMixGeo';
import styles from './OrderAnalytics.module.css';

function SalesMixAnalysis() {
    const { appliedFilters, orders } = useFilters();
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [geoMetricSort, setGeoMetricSort] = useState('volume'); // 'volume' or 'value'
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const dynamicCategories = useMemo(() => {
        const catMap = {};
        orders.forEach(order => {
            order.items.forEach(item => {
                if (!catMap[item.categoryId] && item.categoryId) {
                    catMap[item.categoryId] = item.categoryName || 'Unknown Category';
                }
            });
        });
        return Object.entries(catMap).map(([id, name]) => ({ id, name }));
    }, [orders]);

    // Filter orders based on applied filters
    const filteredOrders = useMemo(() => {
        const filters = {
            startDate: appliedFilters.dateRange?.startDate
                ? appliedFilters.dateRange.startDate.toISOString().split('T')[0]
                : null,
            endDate: appliedFilters.dateRange?.endDate
                ? appliedFilters.dateRange.endDate.toISOString().split('T')[0]
                : null,
            cities: appliedFilters.cities,
            stores: appliedFilters.stores,
            pincodes: appliedFilters.pincodes,
        };

        return applyMultiLevelFilters(orders, filters);
    }, [appliedFilters]);

    // Aggregate product data from orders
    const productData = useMemo(() => {
        const productMap = {};

        // Aggregate from orders
        filteredOrders.forEach(order => {
            order.items.forEach(item => {
                if (!productMap[item.productId]) {
                    productMap[item.productId] = {
                        id: item.productId,
                        name: item.productName || 'Unknown Product',
                        categoryId: item.categoryId || 'unknown',
                        categoryName: item.categoryName || 'Unknown Category',
                        price: item.price || 0,
                        revenue: 0,
                        volume: 0,
                        volumeKg: 0,
                        orderCount: 0,
                    };
                }
                productMap[item.productId].revenue += item.total;
                productMap[item.productId].volume += item.quantity;
                productMap[item.productId].volumeKg += (item.unitQuantity || 0) * (item.quantity || 1);
                productMap[item.productId].orderCount += 1;
            });
        });

        // Calculate totals and percentages
        let allProducts = Object.values(productMap).filter(p => p.volume > 0);

        // Filter by category if selected
        if (selectedCategory !== 'all') {
            allProducts = allProducts.filter(p => p.categoryId === selectedCategory);
        }

        const totalRevenue = allProducts.reduce((sum, p) => sum + p.revenue, 0);
        const totalVolume = allProducts.reduce((sum, p) => sum + p.volume, 0);

        return allProducts.map((prod, idx) => ({
            ...prod,
            revenuePercent: totalRevenue > 0 ? (prod.revenue / totalRevenue) * 100 : 0,
            volumePercent: totalVolume > 0 ? (prod.volume / totalVolume) * 100 : 0,
            avgPrice: prod.volume > 0 ? prod.revenue / prod.volume : 0,
            avgRatePerKg: prod.volumeKg > 0 ? prod.revenue / prod.volumeKg : 0,
        }));
    }, [filteredOrders, selectedCategory]);

    // Sort products for rankings
    const sortedByVolume = useMemo(() =>
        [...productData].sort((a, b) => b.volume - a.volume),
        [productData]
    );

    const sortedByValue = useMemo(() =>
        [...productData].sort((a, b) => b.revenue - a.revenue),
        [productData]
    );

    // Top 10 and Bottom 10
    const top10ByVolume = sortedByVolume.slice(0, 10);
    const top10ByValue = sortedByValue.slice(0, 10);
    const bottom10ByVolume = sortedByVolume.slice(-10).reverse();
    const bottom10ByValue = sortedByValue.slice(-10).reverse();

    // Overview metrics
    const metrics = useMemo(() => {
        return {
            totalProducts: productData.length,
            totalRevenue: productData.reduce((sum, p) => sum + p.revenue, 0),
            totalVolume: productData.reduce((sum, p) => sum + p.volume, 0),
            avgProductRevenue: productData.length > 0
                ? productData.reduce((sum, p) => sum + p.revenue, 0) / productData.length
                : 0,
            topProduct: sortedByValue[0]?.name || 'N/A',
            topProductRevenue: sortedByValue[0]?.revenue || 0,
        };
    }, [productData, sortedByValue]);

    // Geo insights - Top city/store/pincode
    const geoInsights = useMemo(() => {
        return getTopCityStorePincodeByMetric(filteredOrders, geoMetricSort);
    }, [filteredOrders, geoMetricSort]);

    // Product distribution by city data
    const productDistributionData = useMemo(() => {
        return getProductDistributionByCity(filteredOrders, productData, 10);
    }, [filteredOrders, productData]);

    // Ranked product data for full table
    const rankedProducts = useMemo(() => {
        return sortedByValue.map((prod, idx) => ({
            ...prod,
            revenueRank: idx + 1,
            volumeRank: sortedByVolume.findIndex(p => p.id === prod.id) + 1,
        }));
    }, [sortedByValue, sortedByVolume]);

    // Handle product click
    const handleProductClick = (product) => {
        setSelectedProduct(product);
        setIsDrawerOpen(true);
    };

    // Table columns for ranking tables
    const rankingColumns = [
        { key: 'name', label: 'Product', width: '200px' },
        { key: 'categoryName', label: 'Category' },
        { key: 'volume', label: 'Qty', render: v => formatNumber(v) },
        { key: 'volumeKg', label: 'Wt (Kg)', render: v => v > 0 ? formatNumber(v, { decimals: 2 }) : 'N/A' },
        { key: 'revenue', label: 'Revenue', render: v => formatCurrency(v) },
    ];

    // Full table columns
    const fullTableColumns = [
        { key: 'revenueRank', label: '#Rev' },
        { key: 'volumeRank', label: '#Vol' },
        { key: 'name', label: 'Product', width: '180px' },
        { key: 'categoryName', label: 'Category' },
        { key: 'revenue', label: 'Revenue', render: v => formatCurrency(v) },
        { key: 'volume', label: 'Qty' },
        { key: 'volumeKg', label: 'Weight (Kg)', render: v => v > 0 ? formatNumber(v, { decimals: 2 }) : 'N/A' },
        { key: 'revenuePercent', label: '% Revenue', render: v => formatPercentage(v) },
        { key: 'volumePercent', label: '% Qty', render: v => formatPercentage(v) },
        { key: 'avgPrice', label: 'Avg Price/Unit', render: v => formatCurrency(v) },
        { key: 'avgRatePerKg', label: 'Rate/Kg', render: v => v > 0 ? `₹${v.toFixed(0)}/kg` : 'N/A' },
        { key: 'orderCount', label: 'Orders' },
    ];

    // Distribution table columns
    const distributionColumns = [
        { key: 'name', label: 'Product', width: '180px' },
        { key: 'categoryName', label: 'Category' },
        { key: 'revenue', label: 'Total Revenue', render: (v) => formatCurrency(v) },
        { key: 'volume', label: 'Total Volume' },
        { key: 'topCityName', label: 'Top City' },
        { key: 'topCityRevenue', label: 'City Revenue', render: (v) => formatCurrency(v) },
        { key: 'topCityVolume', label: 'City Volume' },
        { key: 'topCityShare', label: 'City Share', render: (v) => formatPercentage(v) },
    ];

    // Render mini horizontal bar chart with click handler
    const renderHorizontalBar = (data, dataKey, color, title, icon) => (
        <ChartCard title={title} icon={icon} height={280}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} layout="vertical" margin={{ left: 10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis
                        type="number"
                        tick={{ fontSize: 9 }}
                        tickFormatter={(v) => dataKey === 'revenue' ? `₹${(v / 1000).toFixed(0)}K` : v}
                    />
                    <YAxis
                        dataKey="name"
                        type="category"
                        tick={{ fontSize: 9 }}
                        width={100}
                        tickFormatter={(v) => v.length > 15 ? v.slice(0, 15) + '...' : v}
                    />
                    <Tooltip
                        formatter={(value) => dataKey === 'revenue' ? formatCurrency(value) : formatNumber(value)}
                        contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '8px' }}
                    />
                    <Legend />
                    <Bar
                        name={dataKey === 'revenue' ? 'Revenue' : 'Volume'}
                        dataKey={dataKey}
                        fill={color}
                        radius={[0, 4, 4, 0]}
                        cursor="pointer"
                        onClick={(barData) => {
                            if (barData && barData.payload) {
                                handleProductClick(barData.payload);
                            }
                        }}
                    />
                </BarChart>
            </ResponsiveContainer>
        </ChartCard>
    );


    // Render clickable ranking table
    const renderClickableTable = (data, title, icon, colorClass, dataType) => (
        <div style={{
            backgroundColor: 'var(--color-card)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--spacing-4)',
            border: '1px solid var(--color-border)',
        }}>
            <h4 style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                marginBottom: 'var(--spacing-3)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)',
                color: colorClass,
            }}>
                {icon} {title}
            </h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-xs)' }}>
                <thead>
                    <tr style={{ background: 'linear-gradient(to right, var(--color-success), var(--color-success-dark))', borderBottom: '1px solid var(--color-border)' }}>
                        <th style={{ padding: '8px', textAlign: 'left', color: 'white' }}>#</th>
                        <th style={{ padding: '8px', textAlign: 'left', color: 'white' }}>Product</th>
                        <th style={{ padding: '8px', textAlign: 'right', color: 'white' }}>{dataType === 'volume' ? 'Volume' : 'Revenue'}</th>
                        <th style={{ padding: '8px', textAlign: 'right', color: 'white' }}>Share</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((prod, idx) => (
                        <tr
                            key={prod.id}
                            onClick={() => handleProductClick(prod)}
                            style={{
                                borderBottom: '1px solid var(--color-border)',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-background)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <td style={{ padding: '8px', fontWeight: 600 }}>{idx + 1}</td>
                            <td style={{ padding: '8px' }}>{prod.name.slice(0, 25)}</td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>
                                {dataType === 'volume' ? formatNumber(prod.volume) : formatCurrency(prod.revenue, { compact: true })}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>
                                {formatPercentage(dataType === 'volume' ? prod.volumePercent : prod.revenuePercent)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <h1 className={styles.title}>Sales Mix Analysis</h1>
                <p className={styles.subtitle}>Analyze product performance and sales distribution</p>
            </header>

            {/* Top Consumption Summary */}
            <section className={styles.section}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 'var(--spacing-3)',
                    flexWrap: 'wrap',
                    gap: 'var(--spacing-2)',
                }}>
                    <h2 className={styles.sectionTitle} style={{ margin: 0 }}>
                        <MapPin size={20} className={styles.sectionIcon} />
                        Top Consumption Summary
                    </h2>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-2)',
                        fontSize: 'var(--font-size-xs)',
                    }}>
                        <span style={{ color: geoMetricSort === 'volume' ? 'var(--color-primary)' : 'var(--color-text-secondary)' }}>Volume</span>
                        <button
                            onClick={() => setGeoMetricSort(prev => prev === 'volume' ? 'value' : 'volume')}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: 'var(--color-primary)',
                                padding: 0,
                            }}
                        >
                            {geoMetricSort === 'volume' ? <ToggleLeft size={28} /> : <ToggleRight size={28} />}
                        </button>
                        <span style={{ color: geoMetricSort === 'value' ? 'var(--color-primary)' : 'var(--color-text-secondary)' }}>Value</span>
                    </div>
                </div>

                <div className={styles.kpiGrid3}>
                    {/* Top City */}
                    <InsightCard
                        title="Top City"
                        icon={MapPin}
                        primaryLabel={geoMetricSort === 'volume' ? "Leading Market (Vol)" : "Leading Market (Revenue)"}
                        primaryValue={geoInsights.topCity?.name || 'N/A'}
                        secondaryStats={[
                            { label: geoMetricSort === 'volume' ? 'Volume' : 'Revenue', value: geoMetricSort === 'volume' ? formatNumber(geoInsights.topCity?.volume || 0) : formatCurrency(geoInsights.topCity?.value || 0, { compact: true }) },
                            { label: 'Orders', value: formatNumber(geoInsights.topCity?.orders || 0) },
                        ]}
                        color="primary"
                        compact
                    />

                    {/* Top Store */}
                    <InsightCard
                        title="Top Store"
                        icon={Store}
                        primaryLabel={geoMetricSort === 'volume' ? "Best Store (Vol)" : "Best Store (Revenue)"}
                        primaryValue={geoInsights.topStore?.name || 'N/A'}
                        secondaryStats={[
                            { label: geoMetricSort === 'volume' ? 'Volume' : 'Revenue', value: geoMetricSort === 'volume' ? formatNumber(geoInsights.topStore?.volume || 0) : formatCurrency(geoInsights.topStore?.value || 0, { compact: true }) },
                            { label: 'Orders', value: formatNumber(geoInsights.topStore?.orders || 0) },
                        ]}
                        color="success"
                        compact
                    />

                    {/* Top Pincode */}
                    <InsightCard
                        title="Top Pincode"
                        icon={Hash}
                        primaryLabel={geoMetricSort === 'volume' ? "Top Pincode (Vol)" : "Top Pincode (Revenue)"}
                        primaryValue={geoInsights.topPincode?.code || 'N/A'}
                        secondaryStats={[
                            { label: geoMetricSort === 'volume' ? 'Volume' : 'Revenue', value: geoMetricSort === 'volume' ? formatNumber(geoInsights.topPincode?.volume || 0) : formatCurrency(geoInsights.topPincode?.value || 0, { compact: true }) },
                            { label: 'Orders', value: formatNumber(geoInsights.topPincode?.orders || 0) },
                        ]}
                        color="secondary"
                        compact
                    />
                </div>
            </section>

            {/* Category Filter */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-3)',
                marginBottom: 'var(--spacing-5)',
                flexWrap: 'wrap',
            }}>
                <Filter size={18} style={{ color: 'var(--color-primary)' }} />
                <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>Category:</span>
                <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--color-border)',
                        backgroundColor: 'var(--color-card)',
                        fontSize: 'var(--font-size-sm)',
                        cursor: 'pointer',
                    }}
                >
                    <option value="all">All Categories</option>
                    {dynamicCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                </select>
            </div>

            {/* Product Overview KPIs */}
            <div className={styles.kpiGrid4}>
                <KPICard
                    title="Active Products"
                    value={formatNumber(metrics.totalProducts)}
                    icon={Package}
                    color="primary"
                />
                <KPICard
                    title="Total Revenue"
                    value={formatCurrency(metrics.totalRevenue, { compact: true })}
                    icon={BarChart3}
                    color="success"
                />
                <KPICard
                    title="Total Volume (Units)"
                    value={formatNumber(metrics.totalVolume)}
                    icon={ShoppingBag}
                    color="secondary"
                />
                <KPICard
                    title="Top Product"
                    value={metrics.topProduct.length > 20 ? metrics.topProduct.slice(0, 20) + '...' : metrics.topProduct}
                    trend={null}
                    icon={Trophy}
                    color="warning"
                />
            </div>

            {/* Top 10 / Bottom 10 Section - 2x2 Grid */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>
                    <Trophy size={20} className={styles.sectionIcon} />
                    Top 10 & Bottom 10 Products
                    <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 400, color: 'var(--color-text-secondary)', marginLeft: 'var(--spacing-2)' }}>
                        (Click product for location breakdown)
                    </span>
                </h2>

                <div className={styles.chartsGrid}>
                    {/* Top 10 by Volume */}
                    {renderHorizontalBar(top10ByVolume, 'volume', '#50C878', 'Top 10 by Volume', TrendingUp)}

                    {/* Top 10 by Value */}
                    {renderHorizontalBar(top10ByValue, 'revenue', '#4A90E2', 'Top 10 by Value', TrendingUp)}

                    {/* Bottom 10 by Volume */}
                    {renderHorizontalBar(bottom10ByVolume, 'volume', '#FFB347', 'Bottom 10 by Volume', TrendingDown)}

                    {/* Bottom 10 by Value */}
                    {renderHorizontalBar(bottom10ByValue, 'revenue', '#EF4444', 'Bottom 10 by Value', TrendingDown)}
                </div>
            </section>

            {/* Ranking Tables */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>
                    <Layers size={20} className={styles.sectionIcon} />
                    Product Rankings
                    <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 400, color: 'var(--color-text-secondary)', marginLeft: 'var(--spacing-2)' }}>
                        (Click for details)
                    </span>
                </h2>

                <div className={styles.chartsGrid}>
                    {/* Top 10 Volume Table */}
                    {renderClickableTable(top10ByVolume, 'Top 10 by Volume', <TrendingUp size={16} />, 'var(--color-success)', 'volume')}

                    {/* Top 10 Value Table */}
                    {renderClickableTable(top10ByValue, 'Top 10 by Value', <TrendingUp size={16} />, 'var(--color-primary)', 'value')}

                    {/* Bottom 10 Volume Table */}
                    {renderClickableTable(bottom10ByVolume, 'Bottom 10 by Volume', <TrendingDown size={16} />, 'var(--color-warning)', 'volume')}

                    {/* Bottom 10 Value Table */}
                    {renderClickableTable(bottom10ByValue, 'Bottom 10 by Value', <TrendingDown size={16} />, 'var(--color-error)', 'value')}
                </div>
            </section>

            {/* Product Distribution by City */}
            <section className={styles.section}>
                <DataTable
                    data={productDistributionData}
                    columns={distributionColumns}
                    title="Product Distribution by City (Top Products)"
                    icon={MapPin}
                    exportFilename="product_distribution_by_city"
                    defaultPageSize={10}
                    onRowClick={handleProductClick}
                />
            </section>

            {/* Full Product Table */}
            <section className={styles.section}>
                <DataTable
                    data={rankedProducts}
                    columns={fullTableColumns}
                    title="Complete Product List"
                    icon={Layers}
                    exportFilename="products_sales_mix"
                    defaultPageSize={10}
                    onRowClick={handleProductClick}
                />
            </section>

            {/* Product Breakdown Drawer */}
            <ProductBreakdownDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                product={selectedProduct}
                filteredOrders={filteredOrders}
                totalRevenue={metrics.totalRevenue}
                totalVolume={metrics.totalVolume}
            />
        </div>
    );
}

export default SalesMixAnalysis;
